import { useCallback, useEffect, useMemo, useState } from 'react';
import StatCard from '../components/StatCard';

const API_BASE = process.env.REACT_APP_API_URL || '';
const KEY_STORAGE = 'btr_admin_api_key';

const STATUS_OPTIONS = [
  ['pending', 'Pending'],
  ['needs_info', 'Needs Info'],
  ['approved', 'Approved'],
  ['rejected', 'Rejected'],
  ['', 'All'],
];

const inputClasses = 'min-h-[44px] rounded-lg border border-outline-variant/30 bg-surface-container-high px-3 py-2.5 text-sm text-on-surface placeholder:text-on-surface-variant/50 transition-colors duration-150 focus:outline-none focus:border-primary/60 focus-visible:ring-2 focus-visible:ring-primary/70';

function StatusPill({ status }) {
  const classes = {
    pending: 'border-secondary/40 bg-secondary/10 text-secondary',
    approved: 'border-primary/40 bg-primary/10 text-primary',
    rejected: 'border-error/40 bg-error/10 text-error',
    needs_info: 'border-tertiary/40 bg-tertiary/10 text-tertiary',
  };

  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 font-label text-[11px] font-semibold uppercase tracking-[0.12em] ${classes[status] || 'border-outline-variant/40 text-on-surface-variant'}`}>
      {status?.replace('_', ' ') || 'unknown'}
    </span>
  );
}

function EmptyState({ label }) {
  return (
    <div className="rounded-xl border border-dashed border-outline-variant/40 bg-surface-container-low p-8 text-center">
      <span className="material-symbols-outlined block text-4xl text-on-surface-variant/60 mb-3">inbox</span>
      <p className="text-sm font-semibold text-on-surface">{label}</p>
    </div>
  );
}

function JsonPreview({ value }) {
  if (!value) return <span className="text-sm text-on-surface-variant/60">No payload</span>;

  let parsed = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      parsed = value;
    }
  }

  if (typeof parsed === 'string') {
    return <span className="text-sm text-on-surface-variant">{parsed}</span>;
  }

  const entries = Object.entries(parsed).filter(([, item]) => item !== '' && item !== null && item !== undefined);
  if (!entries.length) return <span className="text-sm text-on-surface-variant/60">No payload</span>;

  return (
    <dl className="grid grid-cols-1 gap-2">
      {entries.map(([key, item]) => (
        <div key={key} className="rounded-lg border border-outline-variant/20 bg-surface-container-high px-3 py-2">
          <dt className="font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant">{key.replaceAll('_', ' ')}</dt>
          <dd className="mt-0.5 text-sm text-on-surface break-words">
            {Array.isArray(item) ? item.join(', ') : typeof item === 'object' ? JSON.stringify(item) : String(item)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function ReviewControls({ disabled, onReview }) {
  const [notes, setNotes] = useState('');

  function submit(status) {
    onReview(status, notes);
    setNotes('');
  }

  return (
    <div className="space-y-3">
      <textarea
        value={notes}
        rows={2}
        placeholder="Review notes"
        onChange={(event) => setNotes(event.target.value)}
        className={`w-full resize-y ${inputClasses}`}
      />
      <div className="grid grid-cols-3 gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => submit('approved')}
          className="min-h-[40px] rounded-lg bg-primary px-3 py-2 text-[13px] font-bold text-on-primary transition-all duration-150 hover:bg-primary-fixed active:scale-[0.99] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
        >
          Approve
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => submit('needs_info')}
          className="min-h-[40px] rounded-lg border border-tertiary/40 bg-tertiary/10 px-3 py-2 text-[13px] font-bold text-tertiary transition-all duration-150 hover:bg-tertiary/20 active:scale-[0.99] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tertiary/70"
        >
          Needs Info
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => submit('rejected')}
          className="min-h-[40px] rounded-lg border border-error/40 bg-error/10 px-3 py-2 text-[13px] font-bold text-error transition-all duration-150 hover:bg-error/20 active:scale-[0.99] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error/70"
        >
          Reject
        </button>
      </div>
    </div>
  );
}

function ClaimCard({ claim, reviewing, onReview }) {
  return (
    <article className="rounded-xl border border-outline-variant/20 bg-surface-container p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-headline font-expanded text-lg font-bold text-on-surface">{claim.player_name}</h3>
          <p className="mt-0.5 text-[13px] text-on-surface-variant">{claim.club_name} &bull; {claim.league_name}</p>
        </div>
        <StatusPill status={claim.status} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div className="rounded-lg border border-outline-variant/20 bg-surface-container-high p-3">
          <p className="font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant mb-1">Claimant</p>
          <p className="font-semibold text-on-surface">{claim.claimant_name}</p>
          <p className="text-on-surface-variant break-words">{claim.claimant_email}</p>
          <p className="text-on-surface-variant">{claim.relationship}</p>
        </div>
        <div className="rounded-lg border border-outline-variant/20 bg-surface-container-high p-3">
          <p className="font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant mb-1">Evidence</p>
          <p className="font-semibold text-on-surface">{claim.evidence_type || 'Not provided'}</p>
          <p className="text-on-surface-variant break-words">{claim.evidence_value || '—'}</p>
        </div>
      </div>

      {claim.message && (
        <p className="rounded-lg border border-outline-variant/20 bg-surface-container-high p-3 text-sm leading-relaxed text-on-surface-variant">
          {claim.message}
        </p>
      )}

      <ReviewControls
        disabled={reviewing === `claim-${claim.id}` || claim.status !== 'pending'}
        onReview={(status, notes) => onReview(claim.id, status, notes)}
      />
    </article>
  );
}

function RequestCard({ request, reviewing, onReview }) {
  return (
    <article className="rounded-xl border border-outline-variant/20 bg-surface-container p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-headline font-expanded text-lg font-bold text-on-surface">{request.player_name}</h3>
          <p className="mt-0.5 text-[13px] text-on-surface-variant">{request.club_name} &bull; {request.league_name}</p>
          <p className="mt-2 font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-primary">{request.request_type}</p>
        </div>
        <StatusPill status={request.status} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div className="rounded-lg border border-outline-variant/20 bg-surface-container-high p-3">
          <p className="font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant mb-1">Requester</p>
          <p className="font-semibold text-on-surface">{request.requester_name}</p>
          <p className="text-on-surface-variant break-words">{request.requester_email}</p>
          <p className="text-on-surface-variant">{request.relationship}</p>
        </div>
        <div className="rounded-lg border border-outline-variant/20 bg-surface-container-high p-3">
          <p className="font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant mb-2">Requested Fields</p>
          <JsonPreview value={request.payload} />
        </div>
      </div>

      {request.message && (
        <p className="rounded-lg border border-outline-variant/20 bg-surface-container-high p-3 text-sm leading-relaxed text-on-surface-variant">
          {request.message}
        </p>
      )}

      <ReviewControls
        disabled={reviewing === `request-${request.id}` || request.status !== 'pending'}
        onReview={(status, notes) => onReview(request.id, status, notes)}
      />
    </article>
  );
}

export default function AdminProfileModeration() {
  const [apiKey, setApiKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || '');
  const [authKey, setAuthKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || '');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [claims, setClaims] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('claims');

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'x-api-key': authKey,
  }), [authKey]);

  const fetchModeration = useCallback(async () => {
    if (!authKey) return;
    setLoading(true);
    setError('');

    try {
      const query = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const [claimsRes, requestsRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/player-profile/claims${query}`, { headers }),
        fetch(`${API_BASE}/api/admin/player-profile/change-requests${query}`, { headers }),
      ]);

      const [claimsJson, requestsJson] = await Promise.all([
        claimsRes.json().catch(() => null),
        requestsRes.json().catch(() => null),
      ]);

      if (!claimsRes.ok || !claimsJson?.success) throw new Error(claimsJson?.error || `Claims HTTP ${claimsRes.status}`);
      if (!requestsRes.ok || !requestsJson?.success) throw new Error(requestsJson?.error || `Requests HTTP ${requestsRes.status}`);

      setClaims(claimsJson.claims || []);
      setRequests(requestsJson.requests || []);
      sessionStorage.setItem(KEY_STORAGE, authKey);
    } catch (err) {
      setError(err.message);
      setClaims([]);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [authKey, headers, statusFilter]);

  useEffect(() => {
    fetchModeration();
  }, [fetchModeration]);

  async function reviewClaim(id, status, notes) {
    setReviewing(`claim-${id}`);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/admin/player-profile/claims/${id}/review`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ status, review_notes: notes, reviewed_by: 'admin-ui' }),
      });
      const json = await response.json().catch(() => null);
      if (!response.ok || !json?.success) throw new Error(json?.error || `HTTP ${response.status}`);
      await fetchModeration();
    } catch (err) {
      setError(err.message);
    } finally {
      setReviewing('');
    }
  }

  async function reviewRequest(id, status, notes) {
    setReviewing(`request-${id}`);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/admin/player-profile/change-requests/${id}/review`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ status, review_notes: notes, reviewed_by: 'admin-ui' }),
      });
      const json = await response.json().catch(() => null);
      if (!response.ok || !json?.success) throw new Error(json?.error || `HTTP ${response.status}`);
      await fetchModeration();
    } catch (err) {
      setError(err.message);
    } finally {
      setReviewing('');
    }
  }

  const currentItems = activeTab === 'claims' ? claims : requests;
  const filterLabel = STATUS_OPTIONS.find(([value]) => value === statusFilter)?.[1] || 'All';

  function activateKeyAndFetch() {
    if (apiKey === authKey) {
      fetchModeration();
      return;
    }
    setAuthKey(apiKey);
    sessionStorage.setItem(KEY_STORAGE, apiKey);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-5">
        <div>
          <p className="font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant mb-2">Admin</p>
          <h2 className="font-headline font-expanded text-3xl sm:text-4xl font-bold tracking-tight text-on-surface">Profile Moderation</h2>
          <p className="text-sm text-on-surface-variant mt-2 max-w-2xl leading-relaxed">
            Review player profile claims, corrections, social links, and privacy requests before anything becomes trusted.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="password"
            value={apiKey}
            placeholder="Admin API key"
            onChange={(event) => setApiKey(event.target.value)}
            className={`w-full sm:w-72 ${inputClasses}`}
          />
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className={inputClasses}
          >
            {STATUS_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={activateKeyAndFetch}
            disabled={!apiKey || loading}
            className="min-h-[44px] rounded-lg bg-primary px-5 text-sm font-bold text-on-primary transition-all duration-150 hover:bg-primary-fixed active:scale-[0.99] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-error/40 bg-error-container/50 px-4 py-3 text-sm font-semibold text-on-error-container">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Claims" value={claims.length} />
        <StatCard label="Requests" value={requests.length} />
        <StatCard label="Filter" value={filterLabel} />
      </div>

      <div className="flex gap-2 border-b border-outline-variant/20">
        {[
          ['claims', `Claims (${claims.length})`],
          ['requests', `Requests (${requests.length})`],
        ].map(([tab, label]) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`-mb-px min-h-[44px] border-b-2 px-4 py-3 text-sm font-semibold tabular-nums transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 ${
              activeTab === tab
                ? 'border-primary text-primary'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {!authKey ? (
        <EmptyState label="Enter the admin API key to load moderation queues." />
      ) : loading ? (
        <div className="flex items-center justify-center py-20">
          <span className="material-symbols-outlined text-5xl text-primary animate-spin">progress_activity</span>
        </div>
      ) : currentItems.length === 0 ? (
        <EmptyState label={`No ${activeTab} found for this filter.`} />
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          {activeTab === 'claims'
            ? claims.map((claim) => (
              <ClaimCard key={claim.id} claim={claim} reviewing={reviewing} onReview={reviewClaim} />
            ))
            : requests.map((request) => (
              <RequestCard key={request.id} request={request} reviewing={reviewing} onReview={reviewRequest} />
            ))}
        </div>
      )}
    </div>
  );
}
